import camelcaseKeys from "camelcase-keys";
import {
  getConversationDetail,
  getConversationPage,
  onCreateConversation,
  onDeleteConversation,
  onUpdateConversation,
} from "services/apis/conversation";
import snakecaseKeys from "snakecase-keys";
import { listenFirestoreConversationEvent } from "useFirebase/useFireStore";

import { AuthInfo } from "./auth";

const {
  createSlice,
  createAsyncThunk,
  createSelector,
} = require("@reduxjs/toolkit");

export const doCreateConversation = createAsyncThunk(
  "conversation/doCreateConversation",
  async params => {
    const request = snakecaseKeys(params);
    const result = await onCreateConversation(request);
    if (!result) {
      return;
    }
    return camelcaseKeys(result);
  }
);

export const doGetConversationPage = createAsyncThunk(
  "conversation/doGetConversationPage",
  async (params, { dispatch }) => {
    const request = snakecaseKeys(params);
    const result = await getConversationPage(request);
    if (!result) {
      return;
    }
    // Set page number
    await dispatch(setPageNumber(result.data.pageNumber));
    // Set total elements
    await dispatch(setTotalElements(result.data.totalElements));

    return result;
  }
);

export const doGetConversationEvent = createAsyncThunk(
  "conversation/doGetConversationEvent",
  async (params, { dispatch }) => {
    const { workspaceId } = params;
    await listenFirestoreConversationEvent(workspaceId, dispatch);
  }
);

export const doGetConversationDetail = createAsyncThunk(
  "conversation/doGetConversationDetail",
  async params => {
    const request = snakecaseKeys(params);
    const result = await getConversationDetail(request);
    if (!result) {
      return;
    }

    return camelcaseKeys(result);
  }
);

export const doUpdateConversation = createAsyncThunk(
  "conversation/doUpdateConversation",
  async params => {
    const request = snakecaseKeys(params);
    const result = await onUpdateConversation(request);
    if (!result) {
      return;
    }
    return camelcaseKeys(result);
  }
);

export const doDeleteConversation = createAsyncThunk(
  "conversation/doDeleteConversation",
  async params => {
    const request = snakecaseKeys(params);
    const result = await onDeleteConversation(request);
    if (!result) {
      return;
    }
    return camelcaseKeys(result);
  }
);

const initialState = {
  isSaving: false,
  isRefresh: false,
  conversationList: [],
  conversationDetail: {},
  conversationSelected: {},
  params: {},
  isUnlockConversation: null,
  newConversation: {},
  isLockConversation: false,
  isCurrentLockConversation: false,
};

const slice = createSlice({
  name: "conversation",
  initialState: initialState,
  reducers: {
    setCurrentLockConversation: (state, action) => {
      state.isCurrentLockConversation = action.payload;
    },
    setIsLockConversation: (state, action) => {
      state.isLockConversation = action.payload;
    },
    setPageNumber: (state, action) => {
      state.pageNumber = action.payload;
    },
    setTotalElements: (state, action) => {
      state.totalElements = action.payload;
    },
    setIsRefresh: (state, action) => {
      state.isRefresh = action.payload;
    },
    setConversationSelected: (state, action) => {
      state.conversationSelected = action.payload;
    },
    setParamsConversation: (state, action) => {
      state.params = action.payload;
    },
    setConversationList: (state, action) => {
      state.conversationList = [...action.payload];
    },
    changeConversationList: (state, action) => {
      state.newConversation = action.payload;
    },
  },
  extraReducers: {
    [doGetConversationDetail.fulfilled]: (state, action) => {
      state.conversationDetail = action.payload;
    },
    [doGetConversationPage.pending]: state => {
      state.isSaving = true;
    },
    [doGetConversationPage.fulfilled]: state => {
      state.isSaving = false;
    },
    [doGetConversationPage.rejected]: state => {
      state.isSaving = false;
    },
  },
});

const { reducer, actions } = slice;
const { setPageNumber, setTotalElements } = actions;
export const {
  setIsRefresh,
  setConversationSelected,
  changeConversationList,
  setConversationList,
  setParamsConversation,
  setIsLockConversation,
  setCurrentLockConversation,
} = actions;

export default reducer;

export const isSaving = createSelector(
  state => state.conversation.isSaving,
  isSaving => isSaving
);

export const conversationDetail = createSelector(
  state => state.conversation.conversationDetail,
  conversationDetail => conversationDetail
);

export const isRefreshConversation = createSelector(
  state => state.conversation.isRefresh,
  isRefresh => isRefresh
);

export const getConversationSelected = createSelector(
  state => state.conversation.conversationSelected,
  conversationSelected => conversationSelected
);

export const pageNumber = createSelector(
  state => state.conversation.pageNumber,
  pageNumber => pageNumber
);

export const totalEls = createSelector(
  state => state.conversation.totalElements,
  totalElements => totalElements
);

export const filterStatus = createSelector(
  state => state.conversation.params.filterConversation,
  filterConversation => filterConversation
);

export const getConversationList = createSelector(
  state => state.conversation.conversationList,
  conversationList => conversationList
);

export const lockConversation = createSelector(
  state => state.conversation.isLockConversation,
  isLockConversation => isLockConversation
);

export const getNewConversation = createSelector(
  state => state.conversation.newConversation,
  newConversation => newConversation
);

export const newConversation = createSelector(
  state => state.conversation.newConversation,
  newConversation => newConversation
);

export const isLockCurrentConversation = createSelector(
  state => state.conversation.isCurrentLockConversation,
  isCurrentLockConversation => isCurrentLockConversation
);

export const selectIsLockNotCurrentSessionConversation = createSelector(
  newConversation,
  AuthInfo,
  (newConversation, AuthInfo) => {
    return (
      newConversation?.lock &&
      AuthInfo?.sessionId !== newConversation?.lockSessionId
    );
  }
);

export const selectIsLockUserIdConversation = createSelector(
  newConversation,
  AuthInfo,
  (newConversation, AuthInfo) => {
    return (
      newConversation?.lock && AuthInfo?.id === newConversation?.lockUserId
    );
  }
);
